import { useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitleDefault from '../default/DialogTitleDefault';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EmailIcon from '@mui/icons-material/Email';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteIcon from '@mui/icons-material/Delete';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { showModal } from 'react-redux-modal-provider';
import { ButtonBasic } from '../../components';
import ApiSoftland from '../../services/apis.softland.service';
import NotifyBajaDAService from '../../services/notifyBajaDA.service';
import ModalPreviewEmailDebitoRechazado from './ModalPreviewEmailDebitoRechazado';
import ModalPreviewTicketDebitoRechazado from './ModalPreviewTicketDebitoRechazado';

const STEPS = {
    UPLOAD: 'upload',
    REVIEW: 'review',
    LOADING: 'loading',
    RESULT: 'result',
};

const trimStr = (v) => (v === undefined || v === null ? '' : v.toString().trim());

const ModalBajaDebitoRechazado = ({ show, hideModal, hasProcesoActivo = false }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const inputRef = useRef(null);
    const [step, setStep] = useState(STEPS.UPLOAD);
    const [rows, setRows] = useState([]);
    const [fileName, setFileName] = useState('');
    const [missing, setMissing] = useState([]);
    const [resultRows, setResultRows] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isRegistering, setIsRegistering] = useState(false);
    const [isParsing, setIsParsing] = useState(false);
    const [confirmDeleteRow, setConfirmDeleteRow] = useState({ open: false, idx: null, contrato: '' });
    const [templates, setTemplates] = useState({ email: null, ticket: null });
    const handleClose = () => hideModal();

    const resetAll = () => {
        setRows([]);
        setFileName('');
        setMissing([]);
        setResultRows([]);
        setStep(STEPS.UPLOAD);
    };

    const parseFile = (file) => {
        if (!file) return;
        const ext = file.name?.split('.').pop()?.toLowerCase();
        if (!['xlsx', 'xls'].includes(ext)) {
            toast.error('El archivo debe tener extensión .xlsx o .xls');
            return;
        }
        setIsParsing(true);
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const wb = XLSX.read(data, { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                if (!ws) {
                    toast.error('El archivo no contiene hojas válidas.');
                    setIsParsing(false);
                    return;
                }
                const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
                if (!Array.isArray(json) || json.length < 2) {
                    toast.error('El archivo no contiene filas para procesar.');
                    setIsParsing(false);
                    return;
                }
                const dataRows = json.slice(1)
                    .map((r, idx) => ({
                        idx,
                        idSoftland: trimStr(r[0]),
                        contrato: trimStr(r[1]),
                        motivo: trimStr(r[2]),
                    }))
                    .filter((r) => r.idSoftland || r.contrato || r.motivo);
                if (dataRows.length === 0) {
                    toast.error('No se encontraron filas con datos.');
                    setIsParsing(false);
                    return;
                }
                setRows(dataRows);
                setFileName(file.name);
                setMissing([]);
                setStep(STEPS.REVIEW);
            } catch (err) {
                console.log({ errParseExcel: err });
                toast.error('No se pudo leer el archivo Excel.');
            } finally {
                setIsParsing(false);
            }
        };
        reader.onerror = () => {
            toast.error('Error al leer el archivo.');
            setIsParsing(false);
        };
        reader.readAsArrayBuffer(file);
    };

    const onFileChange = (e) => {
        const file = e.target.files?.[0];
        parseFile(file);
        if (inputRef.current) inputRef.current.value = '';
    };

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (isParsing) return;
        const file = e.dataTransfer.files?.[0];
        parseFile(file);
    };

    const onCellChange = (idx, field, value) => {
        setRows((prev) => prev.map((r) => (r.idx === idx ? { ...r, [field]: value } : r)));
    };

    const onDeleteRow = (idx) => {
        const row = rows.find((r) => r.idx === idx);
        if (row) {
            const contrato = trimStr(row.contrato);
            if (missing.includes(contrato)) {
                setMissing((prev) => prev.filter((m) => m !== contrato));
            }
        }
        setRows((prev) => prev.filter((r) => r.idx !== idx));
    };

    const onRequestDeleteRow = (idx) => {
        const row = rows.find((r) => r.idx === idx);
        setConfirmDeleteRow({ open: true, idx, contrato: row ? trimStr(row.contrato) : '' });
    };

    const onConfirmDeleteRow = () => {
        onDeleteRow(confirmDeleteRow.idx);
        setConfirmDeleteRow({ open: false, idx: null, contrato: '' });
    };

    const invalidCount = useMemo(
        () => rows.filter((r) => !trimStr(r.contrato) || !trimStr(r.motivo)).length,
        [rows]
    );

    const onConfirm = async () => {
        const items = rows.map((r) => ({
            idSoftland: trimStr(r.idSoftland),
            contrato: trimStr(r.contrato),
            motivo: trimStr(r.motivo),
        }));
        setStep(STEPS.LOADING);
        setMissing([]);
        try {
            const resp = await ApiSoftland.lookupDebitoRechazado(items);
            const data = resp?.data || [];
            setResultRows(data);
            setTemplates({
              email: resp?.templateEmail || null,
              ticket: resp?.templateTicket || null
            });
            setStep(STEPS.RESULT);
        } catch (err) {
          console.log({err})
            const respMissing = err?.missing;
            const msg = err?.msg || 'Ocurrió un error al consultar Softland.';
            if (Array.isArray(respMissing) && respMissing.length > 0) {
                setMissing(respMissing);
                toast.error(`${msg} (${respMissing.length} contratos no encontrados)`);
            } else {
                toast.error(msg);
            }
            setStep(STEPS.REVIEW);
        }
    };

    const onVerEmail = (row) => showModal(ModalPreviewEmailDebitoRechazado, { row, template: templates.email });
    const onVerTicket = (row) => showModal(ModalPreviewTicketDebitoRechazado, { row, template: templates.ticket });

    const onRegistrarGrupo = async () => {
        if (!resultRows.length) return;
        if (hasProcesoActivo) {
            toast.warning('Hay un proceso EN PROGRESO. Esperá a que finalice antes de registrar un nuevo grupo.');
            return;
        }
        setIsRegistering(true);
        try {
            const resp = await NotifyBajaDAService.createGroup(resultRows);
            const keyGroup = resp?.data?.keyGroup || resp?.keyGroup;
            const total = resp?.data?.total ?? resp?.total ?? resultRows.length;
            toast.success(`Grupo registrado correctamente. Key: ${keyGroup} (${total} registros)`);
            handleClose();
        } catch (err) {
            const msg = err?.response?.data?.msg || 'No se pudo registrar el grupo.';
            toast.error(msg);
        } finally {
            setIsRegistering(false);
        }
    };

    const renderUpload = () => (
        <Box
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            sx={{
                mt: 2,
                p: 4,
                borderRadius: 3,
                border: `2px dashed ${isDragging ? (isDarkMode ? '#1565c0' : '#1976d2') : (isDarkMode ? '#3d4a62' : '#bdbdbd')}`,
                background: isDragging
                    ? (isDarkMode ? '#1a2f4a' : '#e3f2fd')
                    : (isDarkMode ? '#253147' : '#fafafa'),
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.18s ease',
            }}
            onClick={() => !isParsing && inputRef.current?.click()}
        >
            <CloudUploadIcon sx={{ fontSize: 56, color: isDarkMode ? '#90caf9' : '#1976d2' }} />
            <Typography sx={{ mt: 1, fontWeight: 'bold', color: isDarkMode ? '#fff' : '#111' }}>
                Cargar archivo Excel
            </Typography>
            <Typography sx={{ fontSize: 13, color: isDarkMode ? '#9aa8be' : '#666', mt: 0.5 }}>
                Arrastrá el archivo aquí o hacé clic para seleccionarlo.
            </Typography>
            <Typography sx={{ fontSize: 12, color: isDarkMode ? '#9aa8be' : '#888', mt: 1 }}>
                Columnas requeridas: <b>ID Softland</b> | <b>Contrato</b> | <b>Motivo de rechazo</b>
            </Typography>
            <input
                ref={inputRef}
                type='file'
                accept='.xlsx,.xls'
                hidden
                onChange={onFileChange}
            />
        </Box>
    );

    const renderReview = () => (
        <Box sx={{ mt: 1 }}>
            <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Chip
                    label={`Archivo: ${fileName}`}
                    size='small'
                    sx={{ background: isDarkMode ? '#253147' : '#e3f2fd', color: isDarkMode ? '#fff' : '#0d47a1' }}
                />
                <Chip label={`Total: ${rows.length}`} size='small' color='primary' />
                <Chip
                    label={`Inválidas: ${invalidCount}`}
                    size='small'
                    color={invalidCount > 0 ? 'error' : 'success'}
                />
                {missing.length > 0 && (
                    <Chip
                        label={`No encontrados: ${missing.length}`}
                        size='small'
                        sx={{ background: isDarkMode ? '#1565c0' : '#1976d2', color: '#fff' }}
                    />
                )}
                <Box sx={{ flex: 1 }} />
                <Tooltip title='Volver a subir Excel'>
                    <IconButton size='small' onClick={resetAll} sx={{ color: isDarkMode ? '#fff' : '#555' }}>
                        <RestartAltIcon />
                    </IconButton>
                </Tooltip>
            </Stack>

            {missing.length > 0 && (
                <Alert severity='error' sx={{ mb: 1 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 13 }}>
                        Contratos no encontrados en Softland ({missing.length}):
                    </Typography>
                    <Typography sx={{ fontSize: 12, mt: 0.5, wordBreak: 'break-word' }}>
                        {missing.join(', ')}
                    </Typography>
                    <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                        Las filas marcadas en naranja tienen el inconveniente. Editá el número de contrato en la celda o eliminá la fila con el ícono de borrar.
                    </Typography>
                </Alert>
            )}

            <TableContainer component={Paper} sx={{ maxHeight: 420, background: isDarkMode ? '#253147' : '#fff' }}>
                <Table stickyHeader size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f0f0f0', color: isDarkMode ? '#fff' : '#111', fontWeight: 'bold' }}>#</TableCell>
                            <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f0f0f0', color: isDarkMode ? '#fff' : '#111', fontWeight: 'bold' }}>ID Softland</TableCell>
                            <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f0f0f0', color: isDarkMode ? '#fff' : '#111', fontWeight: 'bold' }}>Contrato</TableCell>
                            <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f0f0f0', color: isDarkMode ? '#fff' : '#111', fontWeight: 'bold' }}>Motivo de rechazo</TableCell>
                            <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f0f0f0', color: isDarkMode ? '#fff' : '#111', fontWeight: 'bold', width: 70 }}>Estado</TableCell>
                            <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f0f0f0', width: 48 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r, i) => {
                            const invalidContrato = !trimStr(r.contrato);
                            const invalidMotivo = !trimStr(r.motivo);
                            const isInvalid = invalidContrato || invalidMotivo;
                            const isMissing = missing.includes(trimStr(r.contrato));
                            const rowBg = isMissing
                                ? (isDarkMode ? '#3a2a10' : '#fff3e0')
                                : isInvalid
                                    ? (isDarkMode ? '#4a1f1f' : '#ffebee')
                                    : 'inherit';
                            return (
                                <TableRow key={r.idx} sx={{ background: rowBg }}>
                                    <TableCell sx={{ color: isDarkMode ? '#fff' : '#111' }}>{i + 1}</TableCell>
                                    <TableCell>
                                        <TextField
                                            size='small'
                                            value={r.idSoftland}
                                            onChange={(e) => onCellChange(r.idx, 'idSoftland', e.target.value)}
                                            variant='standard'
                                            sx={{ input: { color: isDarkMode ? '#fff' : '#111', fontSize: 13 } }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction='row' alignItems='center' spacing={0.5}>
                                            <TextField
                                                size='small'
                                                value={r.contrato}
                                                error={invalidContrato || isMissing}
                                                onChange={(e) => onCellChange(r.idx, 'contrato', e.target.value)}
                                                variant='standard'
                                                sx={{ input: { color: isDarkMode ? '#fff' : '#111', fontSize: 13 } }}
                                            />
                                            {isMissing && (
                                                <Tooltip title='No encontrado en Softland'>
                                                    <WarningAmberIcon fontSize='small' sx={{ color: isDarkMode ? '#90caf9' : '#1976d2', flexShrink: 0 }} />
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size='small'
                                            value={r.motivo}
                                            error={invalidMotivo}
                                            onChange={(e) => onCellChange(r.idx, 'motivo', e.target.value)}
                                            variant='standard'
                                            fullWidth
                                            sx={{ input: { color: isDarkMode ? '#fff' : '#111', fontSize: 13 } }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {isMissing ? (
                                            <Tooltip title='No encontrado en Softland'>
                                                <WarningAmberIcon fontSize='small' sx={{ color: isDarkMode ? '#90caf9' : '#1976d2' }} />
                                            </Tooltip>
                                        ) : isInvalid ? (
                                            <ErrorIcon fontSize='small' sx={{ color: '#d32f2f' }} />
                                        ) : (
                                            <CheckCircleIcon fontSize='small' sx={{ color: '#2e7d32' }} />
                                        )}
                                    </TableCell>
                                    <TableCell align='center' sx={{ p: 0.5 }}>
                                        <Tooltip title='Eliminar fila'>
                                            <IconButton size='small' onClick={() => onRequestDeleteRow(r.idx)} sx={{ color: isDarkMode ? '#f87171' : '#d32f2f' }}>
                                                <DeleteIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const renderLoading = () => (
        <Box
            sx={{
                py: 7,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
            }}
        >
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                    size={72}
                    thickness={3}
                    sx={{ color: isDarkMode ? '#3d4a62' : '#e0e0e0' }}
                    variant='determinate'
                    value={100}
                />
                <CircularProgress
                    size={72}
                    thickness={3}
                    sx={{
                        color: isDarkMode ? '#90caf9' : '#1976d2',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        animationDuration: '900ms',
                    }}
                />
                <Box
                    sx={{
                        top: 0, left: 0, bottom: 0, right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography sx={{ fontSize: 11, fontWeight: 'bold', color: isDarkMode ? '#90caf9' : '#1976d2' }}>
                        SOFT
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
                <Typography
                    sx={{
                        fontSize: 17,
                        fontWeight: 'bold',
                        color: isDarkMode ? '#fff' : '#111',
                        mb: 0.5,
                    }}
                >
                    Consultando Softland
                </Typography>
                <Typography
                    sx={{ fontSize: 13, color: isDarkMode ? '#9aa8be' : '#777' }}
                >
                    Buscando información de las direcciones de entrega…
                </Typography>
            </Box>

            <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
                {[0, 1, 2].map((i) => (
                    <Box
                        key={i}
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: isDarkMode ? '#1565c0' : '#1976d2',
                            opacity: 0.3 + i * 0.35,
                            animation: 'pulse 1.4s ease-in-out infinite',
                            animationDelay: `${i * 0.22}s`,
                            '@keyframes pulse': {
                                '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.3 },
                                '40%': { transform: 'scale(1)', opacity: 1 },
                            },
                        }}
                    />
                ))}
            </Stack>
        </Box>
    );

    const renderResult = () => (
        <Box sx={{ mt: 1 }}>
            <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
                <Chip label={`Procesados: ${resultRows.length}`} color='success' size='small' />
                <Box sx={{ flex: 1 }} />
                <Tooltip title='Volver a subir Excel'>
                    <IconButton size='small' onClick={resetAll} sx={{ color: isDarkMode ? '#fff' : '#555' }}>
                        <RestartAltIcon />
                    </IconButton>
                </Tooltip>
            </Stack>
            <TableContainer component={Paper} sx={{ maxHeight: 460, background: isDarkMode ? '#253147' : '#fff' }}>
                <Table stickyHeader size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f0f0f0', color: isDarkMode ? '#fff' : '#111', fontWeight: 'bold' }}>Contrato</TableCell>
                            <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f0f0f0', color: isDarkMode ? '#fff' : '#111', fontWeight: 'bold' }}>Nombre</TableCell>
                            <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f0f0f0', color: isDarkMode ? '#fff' : '#111', fontWeight: 'bold' }}>Motivo</TableCell>
                            <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f0f0f0', color: isDarkMode ? '#fff' : '#111', fontWeight: 'bold', width: 180 }} align='center'>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {resultRows.map((r) => (
                            <TableRow key={r.contrato}>
                                <TableCell sx={{ color: isDarkMode ? '#fff' : '#111' }}>{r.contrato}</TableCell>
                                <TableCell sx={{ color: isDarkMode ? '#fff' : '#111' }}>{r.nombre || '-'}</TableCell>
                                <TableCell sx={{ color: isDarkMode ? '#fff' : '#111' }}>{r.motivo}</TableCell>
                                <TableCell align='center'>
                                    <Stack direction='row' spacing={1} justifyContent='center'>
                                        <Tooltip title='Ver Email'>
                                            <IconButton size='small' onClick={() => onVerEmail(r)} sx={{ color: isDarkMode ? '#90caf9' : '#1976d2' }}>
                                                <EmailIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title='Ver Ticket'>
                                            <IconButton size='small' onClick={() => onVerTicket(r)} sx={{ color: isDarkMode ? '#ce93d8' : '#7b1fa2' }}>
                                                <ConfirmationNumberIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    return (
        <>
        <Dialog open={show} disableEscapeKeyDown maxWidth='lg' fullWidth>
            <Box sx={{ background: isDarkMode ? '#1F2A40' : '#fff' }}>
                <DialogTitleDefault id='baja-debito-rechazado' onClose={handleClose}>
                    <Typography
                        sx={{
                            flex: 1,
                            textAlign: 'center',
                            color: isDarkMode ? '#fff' : '#000',
                            fontWeight: 'bold',
                        }}
                        variant='h5'
                    >
                        BAJA DÉBITO AUTOMÁTICO - DÉBITO RECHAZADO
                    </Typography>
                </DialogTitleDefault>
                <Divider sx={{ background: isDarkMode ? '#3d4a62' : 'gray' }} />
                <DialogContent>
                    {step === STEPS.UPLOAD && renderUpload()}
                    {step === STEPS.REVIEW && renderReview()}
                    {step === STEPS.LOADING && renderLoading()}
                    {step === STEPS.RESULT && renderResult()}
                </DialogContent>
                <Divider sx={{ background: isDarkMode ? '#3d4a62' : 'gray' }} />
                <DialogActions sx={{ p: 2 }}>
                    <ButtonBasic
                        textButton='Cerrar'
                        variant='outlined'
                        color='inherit'
                        onClick={handleClose}
                        isDisabled={isRegistering}
                    />
                    {step === STEPS.REVIEW && (
                        <ButtonBasic
                            textButton={`Confirmar (${rows.length - invalidCount})`}
                            color='primary'
                            isDisabled={invalidCount > 0 || rows.length === 0}
                            onClick={onConfirm}
                        />
                    )}
                    {step === STEPS.RESULT && (
                        <ButtonBasic
                            textButton={isRegistering ? 'Registrando…' : `Registrar grupo (${resultRows.length})`}
                            color='primary'
                            isDisabled={isRegistering || resultRows.length === 0}
                            onClick={onRegistrarGrupo}
                        />
                    )}
                </DialogActions>
            </Box>
        </Dialog>

        <Dialog open={isParsing} maxWidth='xs' fullWidth>
            <Box sx={{ background: isDarkMode ? '#1F2A40' : '#fff', p: 3 }}>
                <Stack alignItems='center' spacing={2}>
                    <CircularProgress size={48} sx={{ color: isDarkMode ? '#90caf9' : '#1976d2' }} />
                    <Typography sx={{ fontWeight: 'bold', color: isDarkMode ? '#e0e0e0' : '#111' }}>
                        Leyendo archivo…
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: isDarkMode ? '#9aa8be' : '#666', textAlign: 'center' }}>
                        Procesando el Excel, por favor esperá.
                    </Typography>
                </Stack>
            </Box>
        </Dialog>

        <Dialog open={confirmDeleteRow.open} onClose={() => setConfirmDeleteRow({ open: false, idx: null, contrato: '' })} maxWidth='xs' fullWidth>
            <DialogTitle sx={{ background: isDarkMode ? '#1F2A40' : '#fff', color: isDarkMode ? '#fff' : '#111' }}>
                Eliminar fila
            </DialogTitle>
            <DialogContent sx={{ background: isDarkMode ? '#1F2A40' : '#fff' }}>
                <DialogContentText sx={{ color: isDarkMode ? '#9aa8be' : '#666' }}>
                    ¿Eliminar la fila del contrato{' '}
                    <strong style={{ fontFamily: 'monospace', color: isDarkMode ? '#90caf9' : '#1976d2' }}>
                        {confirmDeleteRow.contrato || 'sin contrato'}
                    </strong>?
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ background: isDarkMode ? '#1F2A40' : '#fff', p: 2 }}>
                <ButtonBasic
                    textButton='Cancelar'
                    variant='outlined'
                    color='inherit'
                    onClick={() => setConfirmDeleteRow({ open: false, idx: null, contrato: '' })}
                />
                <ButtonBasic
                    textButton='Eliminar'
                    color='error'
                    onClick={onConfirmDeleteRow}
                />
            </DialogActions>
        </Dialog>
        </>
    );
};

export default ModalBajaDebitoRechazado;
